import type { CalendarDate, Instant } from "@hq/contracts";
import {
  addDays,
  calendarDateOf,
  daysBetween,
  type NowContext,
  todayOf,
  wallClockOf,
  weekdayOf,
} from "@hq/core";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "Fri 18 Sep", with the year appended when it differs from the reference year. */
export function formatShortDate(date: CalendarDate, referenceYear?: string): string {
  const year = date.slice(0, 4);
  const month = MONTHS[Number(date.slice(5, 7)) - 1] ?? "";
  const day = Number(date.slice(8, 10));
  const base = `${WEEKDAYS[weekdayOf(date)]} ${day} ${month}`;
  return referenceYear !== undefined && referenceYear !== year ? `${base} ${year}` : base;
}

/** "3:00 PM" from an instant in the given zone. Deterministic: no locale lookup. */
export function formatTime(instant: Instant, timeZone: string): string {
  const { hour, minute } = wallClockOf(instant, timeZone);
  const suffix = hour < 12 ? "AM" : "PM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${minute < 10 ? `0${minute}` : minute} ${suffix}`;
}

/** Relative day word for a calendar date, or undefined when it is not today/tomorrow/yesterday. */
function relativeDay(date: CalendarDate, today: CalendarDate): string | undefined {
  if (date === today) return "Today";
  if (date === addDays(today, 1)) return "Tomorrow";
  if (date === addDays(today, -1)) return "Yesterday";
  return undefined;
}

/**
 * The Deadline label for a row: "Today", "Tomorrow", "Overdue · Mon 14 Sep", or a short date.
 * A deadline is a calendar date; it never shifts with the zone.
 */
export function formatDueDate(date: CalendarDate, now: NowContext): string {
  const today = todayOf(now);
  if (date < today) return `Overdue · ${formatShortDate(date, today.slice(0, 4))}`;
  return relativeDay(date, today) ?? formatShortDate(date, today.slice(0, 4));
}

export function isOverdue(date: CalendarDate, now: NowContext): boolean {
  return date < todayOf(now);
}

/**
 * The Scheduled label for a row, in the device zone: "Today 3:00 PM" today, "Tomorrow 9:00 AM",
 * "Wed 3:00 PM" within the next week, otherwise "Wed 18 Sep 3:00 PM".
 */
export function formatScheduled(instant: Instant, now: NowContext): string {
  const today = todayOf(now);
  const date = calendarDateOf(instant, now.timeZone);
  const time = formatTime(instant, now.timeZone);
  if (date === today) return `Today ${time}`;
  const rel = relativeDay(date, today);
  if (rel) return `${rel} ${time}`;
  const delta = daysBetween(today, date);
  if (delta > 0 && delta < 7) return `${WEEKDAYS[weekdayOf(date)]} ${time}`;
  return `${formatShortDate(date, today.slice(0, 4))} ${time}`;
}

/** "30m", "1h", "1h 30m". */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Day headings for grouped lists: "Today · Wed 16 Sep", "Tomorrow · Thu 17 Sep", "Fri 18 Sep". */
export function formatDayHeading(date: CalendarDate, now: NowContext): string {
  const today = todayOf(now);
  const short = formatShortDate(date, today.slice(0, 4));
  const rel = relativeDay(date, today);
  return rel ? `${rel} · ${short}` : short;
}

/** "16 Sep 2026, 10:00 AM" for created/updated timestamps. */
export function formatTimestamp(instant: Instant, timeZone: string): string {
  const date = calendarDateOf(instant, timeZone);
  const month = MONTHS[Number(date.slice(5, 7)) - 1] ?? "";
  return `${Number(date.slice(8, 10))} ${month} ${date.slice(0, 4)}, ${formatTime(instant, timeZone)}`;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export function isIsoDate(s: string): s is CalendarDate {
  return ISO_DATE.test(s);
}
